import clsx from 'clsx'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

interface INavbar {
  className?: string
}

const ICON = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const

interface INavItem {
  to: string
  label: string
  icon: ReactNode
  // highlighted: this section is open
  active: boolean
  // tapping it again would only push the same page
  current: boolean
}

// The phone menu: five labelled destinations (icons alone read as a puzzle),
// the one creating action in the middle. Favourites moved to the profile menu.
const Navbar: FC<INavbar> = ({ className }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const item = ({ to, label, icon, active, current }: INavItem) => (
    <Link
      key={to}
      to={to}
      aria-current={current ? 'page' : undefined}
      className={clsx(
        // sideways: the label beside the icon, one 44px row
        'flex flex-col short:flex-row flex-1 basis-0 items-center justify-center gap-[3px] short:gap-1.5 h-14 short:h-11 min-w-0 text-[0.6875rem] leading-[0.875rem]',
        active ? 'font-black text-brand-ink' : 'font-bold text-muted',
        current && 'pointer-events-none',
      )}
    >
      <span
        className={clsx(
          'flex items-center justify-center h-7 rounded-full',
          active ? 'w-[3.25rem] bg-brand-soft' : 'w-7',
        )}
      >
        {icon}
      </span>
      <span className="max-w-full truncate">{label}</span>
    </Link>
  )

  return (
    <nav
      aria-label={t('nav.label')}
      className={clsx(
        // the background runs under the home indicator and the notch, the
        // items stay clear of them (--navbar-* in styles/index.css)
        'fixed bottom-0 inset-x-0 z-50 flex items-end h-[var(--navbar-h)] pl-[max(0.25rem,var(--safe-left))] pr-[max(0.25rem,var(--safe-right))] pt-1.5 short:pt-1 pb-[var(--navbar-pad)] bg-surface border-t border-solid border-ink/10 shadow-[0_-0.25rem_1.25rem_rgba(33,36,48,0.06)]',
        className,
      )}
    >
      {item({
        to: '/',
        label: t('map'),
        icon: (
          <svg {...ICON}>
            <path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
        ),
        active: pathname === '/' || pathname.startsWith('/map'),
        current: pathname === '/',
      })}
      {item({
        to: '/cabinet',
        label: t('cabinet'),
        icon: (
          <svg {...ICON}>
            <path d="M3 11l9-7 9 7" />
            <path d="M5 10v10h14V10" />
          </svg>
        ),
        active: pathname === '/cabinet',
        current: pathname === '/cabinet',
      })}
      {/* The plus creates a listing for everyone (38.2) — the market-standard
          meaning of the center action; saving a search lives on the map */}
      <Link
        to={`/create-ad?from=${pathname}`}
        aria-current={pathname === '/create-ad' ? 'page' : undefined}
        className="flex flex-col short:flex-row flex-1 basis-0 items-center justify-end short:justify-center gap-[3px] short:gap-1.5 h-16 short:h-11 min-w-0 text-[0.6875rem] leading-[0.875rem] font-extrabold text-ink"
      >
        <span className="flex items-center justify-center w-12 h-12 short:w-9 short:h-9 rounded-full bg-brand-strong text-white shadow-[0_0.25rem_0.75rem_rgba(60,94,244,0.35)]">
          <svg {...ICON} strokeWidth={2.2}>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
        <span className="max-w-full truncate">{t('nav.add')}</span>
      </Link>
      {item({
        to: '/messenger',
        label: t('nav.chats'),
        icon: (
          <svg {...ICON}>
            <path d="M4 5h16v11H8l-4 4z" />
          </svg>
        ),
        active: pathname.startsWith('/messenger'),
        current: pathname === '/messenger',
      })}
      {item({
        to: '/profile',
        label: t('profile'),
        icon: (
          <svg {...ICON}>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
          </svg>
        ),
        active: pathname === '/profile',
        current: pathname === '/profile',
      })}
    </nav>
  )
}

export default Navbar
